/**
 * Brzi izbor perioda, zajednički za sve spiskove.
 *
 * Izbor ne zamenjuje polja „od" i „do" nego ih popunjava. Tako se uvek vidi koji je tačno
 * opseg aktivan: „ovaj mesec" inače ostavlja otvorenim da li se misli do danas ili do kraja
 * meseca, a to je razlika koju neko posle računa u izveštaju.
 *
 * Nedelja počinje ponedeljkom.
 */
export type DatePreset =
  | "today"
  | "thisWeek"
  | "thisMonth"
  | "thisQuarter"
  | "thisYear"
  | "last3"
  | "last7"
  | "last30"
  | "all";

/** Kalendarski periodi: za spiskove koji se citaju po mesecu i kvartalu, kao finansije i porudzbine. */
export const DATE_PRESETS: DatePreset[] = ["today", "thisWeek", "thisMonth", "thisQuarter", "thisYear"];

/**
 * Pomicni periodi: za radne spiskove, gde pitanje nije „sta je bilo ovog meseca" nego „sta je
 * stiglo u zadnjih nekoliko dana". Prvog u mesecu „ovaj mesec" daje jedan dan, a „poslednjih 30
 * dana" daje mesec — za posao koji se nasledjuje iz smene u smenu, drugo je tacno.
 *
 * Oba kraja su ukljucena, pa „poslednja 3 dana" znaci danas i dva pre njega.
 */
export const ROLLING_PRESETS: DatePreset[] = ["today", "last3", "last7", "last30"];

const pad2 = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m0: number, d: number) => `${y}-${pad2(m0 + 1)}-${pad2(d)}`;
const danaUMesecu = (y: number, m0: number) => new Date(y, m0 + 1, 0).getDate();
const uIso = (d: Date) => iso(d.getFullYear(), d.getMonth(), d.getDate());

/** Opseg za izabrani period, uključujući oba kraja. „all" briše opseg. */
/** Opseg koji se zavrsava danas i ide `dana` unazad, ukljucujuci oba kraja. */
function unazad(now: Date, dana: number): { from: string; to: string } {
  const od = new Date(now);
  od.setDate(now.getDate() - dana);
  return { from: uIso(od), to: uIso(now) };
}

export function presetRange(preset: DatePreset, now: Date): { from: string; to: string } {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case "today":
      return { from: uIso(now), to: uIso(now) };
    case "thisWeek": {
      // getDay() vraća 0 za nedelju, pa se ponedeljak računa unazad.
      const pomak = (now.getDay() + 6) % 7;
      const pon = new Date(now);
      pon.setDate(now.getDate() - pomak);
      const ned = new Date(pon);
      ned.setDate(pon.getDate() + 6);
      return { from: uIso(pon), to: uIso(ned) };
    }
    case "thisMonth":
      return { from: iso(y, m, 1), to: iso(y, m, danaUMesecu(y, m)) };
    case "thisQuarter": {
      const poc = Math.floor(m / 3) * 3;
      const kraj = poc + 2;
      return { from: iso(y, poc, 1), to: iso(y, kraj, danaUMesecu(y, kraj)) };
    }
    case "thisYear":
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case "last3":
      return unazad(now, 2);
    case "last7":
      return unazad(now, 6);
    case "last30":
      return unazad(now, 29);
    case "all":
      return { from: "", to: "" };
  }
}

/** Koji brzi izbor odgovara datom opsegu, ako ijedan. Da dugme ostane označeno posle osvežavanja. */
export function presetForRange(
  from: string,
  to: string,
  now: Date,
  medju: DatePreset[] = DATE_PRESETS,
): DatePreset | null {
  if (!from && !to) return null;
  for (const p of medju) {
    const r = presetRange(p, now);
    if (r.from === from && r.to === to) return p;
  }
  return null;
}
